import { Component, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Role, User } from 'src/app/models';
import { ToastService } from 'src/app/services/toast.service';
import { UserService } from 'src/app/services/user.service';
import { FormUtil } from 'src/app/utils/form.util';
import { MessageUtil } from 'src/app/utils/message.util';
import { StorageUtil } from 'src/app/utils/storage.util';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss']
})
export class UserFormComponent implements OnInit {

  loading = false;
  changePassword = false;
  model: User = new User();
  origin: string;
  userForm: UntypedFormGroup;
  rolesList: { label: string; value: Role; }[];

  constructor(
    public dialogRef: DynamicDialogRef,
    private userService: UserService,
    private messageUtil: MessageUtil,
    private spinner: NgxSpinnerService,
    private toasty: ToastService,
    private config: DynamicDialogConfig
  ) { }

  ngOnInit(): void {
    this.resetForm();
    if (this.config.data.model) {
      // EBUG-426 — cópia, não o objeto da linha da lista: o save() grava nele o que foi digitado, e
      // uma edição recusada (troca de senha de conta usada em outra marina) deixava a senha na
      // linha. Reabrir a mesma conta sem recarregar mandava a senha de novo e a troca só do perfil
      // voltava barrada. O save() só troca campos de primeiro nível, então a cópia rasa basta.
      this.model = { ...this.config.data.model };
      this.origin = this.config.data.origin;
      this.userForm.patchValue(this.model);
      this.changePass();
    }
    this.findRoles();
  }

  passwordMatch(c: AbstractControl): any {
    return c.get('password').value === c.get('confirmPassword').value ? null : { nomatch: true };
  }

  usernameInUse(): void {
    // EBUG-426 — na edição o username é somente leitura: a busca acharia o próprio usuário, e o
    // reset apagava o login do formulário.
    if (this.model && this.model.id) {
      return;
    }
    const typed = (this.userForm.get('username').value || '').trim();
    if (!typed) {
      return;
    }
    this.userService.getByUsername(typed)
      .subscribe(
        (data) => {
          // Compara o username devolvido: o backend antigo cortava o sufixo do path ("joao.silva"
          // chegava como "joao") e podia achar outra conta. O vínculo de conta existente no
          // cadastro está desligado no backend, então o login que já existe segue barrado aqui.
          if (data && data.username && data.username.toLowerCase() === typed.toLowerCase()) {
            Swal.fire('Alerta!', `Usuário ${typed} já cadastrado`, 'warning');
            this.userForm.get('username').reset();
          }
        });
  }

  validPassword(): void {
    if (this.userForm.get('password').value !== this.userForm.get('confirmPassword').value) {
      Swal.fire('Alerta!', 'Senhas não conferem', 'warning');
      this.userForm.get('password').reset();
      this.userForm.get('confirmPassword').reset();
    }
  }

  formValidations(): boolean {
    if (this.userForm.valid) {
      return true;
    } else {
      FormUtil.touchAndSendFormGroup(this.userForm);
      Swal.fire('Alerta!', 'Por favor, preencha os campos obrigatórios', 'warning');
      return false;
    }
  }

  save(): void {
    this.loading = true;
    this.spinner.show();
    if (this.model && this.model.id) {
      Object.keys(this.userForm.controls).forEach((key) => {
        this.model[key] = this.userForm.get(key).value;
      });
      this.userService.update(this.model).subscribe(
        (data) => {
          this.spinner.hide();
          this.toasty.success('Usuário alterado com sucesso!');
          this.dialogRef.close(data);
        },
        (error) => {
          this.spinner.hide();
          const exception = error.error.data.exception;
          this.messageUtil.generateMessage(exception.type, exception.title, exception.message);
        }
      );
    } else {
      this.model = this.userForm.value;
      this.model.marina = StorageUtil.getMarina();
      this.model.marinas = [];
      this.model.marinas.push(StorageUtil.getMarina());
      this.userService.create(this.model).subscribe(
        (data) => {
          this.spinner.hide();
          // EBUG-426 — a resposta é o envelope {data: {user}}. Quando o login já tinha conta no
          // EasyMarine, a API vincula essa conta à marina (existingAccountLinked=true) sem aplicar o
          // nome, o e-mail e a senha digitados, e quem cadastrou precisa saber disso.
          const saved: any = data && (data as any).data && (data as any).data.user;
          if (saved && saved.existingAccountLinked) {
            this.messageUtil.generateMessage('warning', 'SUMMARY.WARNING',
              'O usuário ' + saved.username + ' já tinha conta no EasyMarine e foi vinculado a esta marina. ' +
              'O nome, o e-mail e a senha digitados não foram aplicados: ele entra com a senha que já usa ' +
              'ou pelo "Esqueci minha senha".');
          } else {
            this.toasty.success('Usuário salvo com sucesso!');
          }
          this.dialogRef.close(data);
        },
        (error) => {
          this.spinner.hide();
          const exception = error.error.data.exception;
          this.messageUtil.generateMessage(exception.type, exception.title, exception.message);
        }
      );
    }
  }

  isGestor(): boolean {
    return this.model && this.model.marinas && this.model.marinas.length > 0;
  }

  resetForm(): void {
    this.userForm = new UntypedFormGroup({
      id: new UntypedFormControl(null),
      username: new UntypedFormControl(null, Validators.required),
      password: new UntypedFormControl(null, Validators.required),
      confirmPassword: new UntypedFormControl(null, Validators.required),
      firstName: new UntypedFormControl(null, Validators.required),
      lastName: new UntypedFormControl(null, Validators.required),
      email: new UntypedFormControl(null, Validators.required),
      bracelet: new UntypedFormControl(null),
      changePassword: new UntypedFormControl(false),
      active: new UntypedFormControl(true),
      operator: new UntypedFormControl(false),
      roleId: new UntypedFormControl(null, Validators.required),
      // Permissão de token de integração da API pública. Começa desligada: é concessão explícita,
      // e o token que ela libera não expira.
      integrationTokenEnabled: new UntypedFormControl(false)
    });
  }

  changePass($event?: any): void {
    if ($event && $event.checked) {
      this.userForm.get('password').enable();
      this.userForm.get('confirmPassword').enable();
      this.userForm.get('password').reset();
      this.userForm.get('confirmPassword').reset();
    } else {
      // EBUG-426 — controle desabilitado mantém o valor, e o save() lê controle a controle: sem
      // limpar, desligar "Mudar senha" depois de digitar ainda mandava a senha (a conta desta
      // marina tinha a senha trocada; a compartilhada seguia barrada mesmo trocando só o perfil).
      this.userForm.get('password').setValue(null);
      this.userForm.get('confirmPassword').setValue(null);
      this.userForm.get('password').disable();
      this.userForm.get('confirmPassword').disable();
    }
  }

  async findRoles(): Promise<void> {
    return new Promise<void>(
      async (res) => {
        this.userService.getRolesByHierarchy().subscribe(
          (data) => {
            this.rolesList = data.map((m) => ({ label: m.name, value: m }));
            res();
          },
          (error) => { }

        );

      }
    );
  }

}

